import { DateTime } from "luxon";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { RiBuilding2Fill } from "react-icons/ri";
import { Label } from "~/components/shadcn/Label";
import { MultiSelect } from "~/components/shadcn/MultiSelect";
import { TagInput } from "~/components/TagInput";
import { toastHandler } from "~/components/ToastHandler";
import { EventDate } from "~/components/v3/events-components/EventViewAndEdit/EventDateTime";
import { SubLabel } from "~/components/v3/shadcn/Label";
import TimeInput from "~/components/v3/themed/TimeInput";
import { useCommunity } from "~/hooks/useCommunity";
import { cities } from "~/utils/cities";
import { Button } from "../../components/v3/themed/Button";
import { Input } from "../../components/v3/themed/Input";
import LoginModal from "../../components/v3/themed/LoginModal";
import Modal from "../../components/v3/themed/Modal";
import { SwitchField } from "../../components/v3/themed/Switch";
import { createMultipleEvents } from "../_actions/Events";
import { CreateMultipleEventsInput } from "../_actions/types/MultipleEventsInputs";
import { mergeToTime } from "~/utils/time";
import TimeZoneAutoComplete from "~/components/v3/themed/TimezoneAutoComplete";

type Props = {
    isOpen: boolean;
    setIsOpen: () => void;
    communitySlug?: string;
};

type CreateMultipleEventsForm = Omit<
    CreateMultipleEventsInput,
    "startTime" | "endTime"
> & {
    startTime24h?: string;
    endTime24h?: string;
    startTime?: Date;
    endTime?: Date;
};

const BatchEventsModal = ({ communitySlug, isOpen, setIsOpen }: Props) => {
    const currentDayPlus15 = DateTime.now().plus({ days: 15 }).toJSDate();
    const currentDayPlus15Plus2Hours = DateTime.now()
        .plus({ days: 15, hours: 2 })
        .toJSDate();

    const form = useForm<CreateMultipleEventsForm>({
        defaultValues: {
            startTime: currentDayPlus15,
            endTime: currentDayPlus15,
        },
    });

    const session = useSession();
    const [isMicroAutoApproved, setIsMicroAutoApproved] = useState(false);
    const [isSimulcast, setIsSimulcast] = useState(false);
    const [selectedCities, setSelectedCities] = useState<string[]>([]);
    const tags = form.watch("tags") ?? [];
    const resourceTags = form.watch("resourceTags") ?? [];

    const dateSuffix = new Date()
        .toLocaleString("en-US", { month: "short", year: "numeric" })
        .toLowerCase()
        .split(" ")
        .join("-");

    const { community } = useCommunity();

    useEffect(() => {
        form.setValue("tags", [`${communitySlug}-${dateSuffix}`], {
            shouldDirty: true,
        });
        form.setValue("resourceTags", [`${communitySlug}-${dateSuffix}`], {
            shouldDirty: true,
        });
    }, [isOpen]);

    const { isValid, isSubmitting } = form.formState;

    const cityOptions = cities.map((c) => {
        return { label: c.cityLong, value: c.citySlug };
    });

    const onSubmit = async (data: CreateMultipleEventsForm) => {
        const startTime =
            data.startTime24h && data.startTime
                ? mergeToTime(data.startTime24h, new Date(data.startTime))
                : null;

        const endTime =
            data.endTime && data.endTime24h
                ? mergeToTime(data.endTime24h, new Date(data.endTime))
                : null;

        toastHandler({
            loadingMessage: "Creating events...",
            successMessage: "Events created successfully.",
            errorMessage: "Failed to create events.",
            operation: async () => {
                await createMultipleEvents({
                    name: data.name,
                    communitySlug: communitySlug as string,
                    tags,
                    resourceTags,
                    locations: selectedCities.map((c) => {
                        return { city: c };
                    }),
                    description: data.description,
                    startTime: startTime?.toISOString(),
                    endTime: endTime?.toISOString(),
                    timezone: data.timezone,
                    isSimulcast,
                });
                setIsOpen();
            },
        });
    };

    if (!isOpen) {
        return null;
    }

    if (!session.data?.user) {
        return (
            <LoginModal
                isOpen={true}
                setIsOpen={setIsOpen}
                title="Sign in to propose an event"
                description="You need to have a River account to propose events"
            />
        );
    }

    const handleAddTag = (newTags: string[]) => {
        const updatedTags = [...tags, ...newTags];
        form.setValue("tags", updatedTags, { shouldDirty: true });
    };

    const handleRemoveTag = (tagToRemove: string) => {
        const updatedTags = tags.filter((tag) => tag !== tagToRemove);
        form.setValue("tags", updatedTags, { shouldDirty: true });
    };

    const handleAddResourceTag = (newTags: string[]) => {
        const updatedTags = [...resourceTags, ...newTags];
        form.setValue("resourceTags", updatedTags, { shouldDirty: true });
    };

    const handleRemoveResourceTag = (tagToRemove: string) => {
        const updatedTags = resourceTags.filter((tag) => tag !== tagToRemove);
        form.setValue("resourceTags", updatedTags, { shouldDirty: true });
    };

    return (
        <Modal
            open={isOpen}
            onOpenChange={() => setIsOpen()}
            title="Create multiple events"
            description="Create multiple events for your community."
            icon={<RiBuilding2Fill />}
            footer={
                <>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setIsOpen();
                        }}
                    >
                        Nevermind
                    </Button>
                    <Button
                        variant="default"
                        className={`
                                  w-[53%]
                                  rounded-[26px]
                                  border-2 
                                  border-background 
                                `}
                        size="lg"
                        onClick={form.handleSubmit(onSubmit)}
                        disabled={!isValid || isSubmitting}
                    >
                        Create events
                    </Button>
                </>
            }
        >
            <FormProvider {...form}>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="new-event-name">Events Name</Label>
                        <Input
                            {...form.register("name", {
                                required: true,
                            })}
                            id="new-event-name"
                            type="text"
                            placeholder="The Grand Slam Society"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label htmlFor="new-event-description">
                            Description (optional)
                        </Label>
                        <Input
                            {...form.register("description")}
                            id="new-event-description"
                            type="text-area"
                            placeholder="This is going to be the greatest event ever"
                        />
                    </div>

                    <SwitchField
                        label="Use same event time across timezones (simulcast)"
                        defaultChecked={false}
                        checked={isSimulcast}
                        onCheckedChange={(val) => {
                            setIsSimulcast(val);
                        }}
                    />

                    {isSimulcast && (
                        <div className="flex w-full items-center gap-2">
                            <label>Timezone</label>
                            <TimeZoneAutoComplete<CreateMultipleEventsForm>
                                setValue={form.setValue}
                                initialValue={form.watch("timezone")}
                            />
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <EventDate
                            isEditing
                            isLockedMode={false}
                            value="startTime"
                            valueLocked="startTimeLocked"
                            timeField="startTime24h"
                            label="Start"
                            isViewModeEnabled={false}
                        />
                        <div className="w-1/2 self-end">
                            <TimeInput
                                time={null}
                                formField="startTime24h"
                                isEditMode={true}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2">
                        <EventDate
                            isEditing
                            isLockedMode={false}
                            value="endTime"
                            valueLocked="endTimeLocked"
                            timeField="endTime24h"
                            label="End"
                            isViewModeEnabled={false}
                        />
                        <div className="w-1/2 self-end">
                            <TimeInput
                                time={null}
                                formField="endTime24h"
                                isEditMode={true}
                            />
                        </div>
                    </div>

                    <SwitchField
                        label={`Is this a micro event? (no approval required, max ${community?.microEventMaxCapacity ?? 8} attendees)`}
                        defaultChecked={false}
                        checked={isMicroAutoApproved}
                        onCheckedChange={(val) => {
                            setIsMicroAutoApproved(val);
                        }}
                    />
                    <div className="flex flex-col gap-3">
                        <Label className="flex-grow">Add tags to events</Label>
                        <SubLabel className="w-full text-xs">
                            Event tags are public and can be used by other users
                            to filter events, or get an idea of what is related
                            to the event
                        </SubLabel>
                        <TagInput
                            tags={tags}
                            onAddTag={handleAddTag}
                            onRemoveTag={handleRemoveTag}
                        />
                    </div>
                    <div className="flex flex-col gap-3">
                        <Label className="flex-grow">
                            Add resource tags to events
                        </Label>
                        <SubLabel className="w-full text-xs">
                            Resource tags are hidden and they're used for
                            functionalities such as Event Image Overlays and Tag
                            gated Membership events
                        </SubLabel>
                        <TagInput
                            tags={form.watch("resourceTags") ?? []}
                            onAddTag={handleAddResourceTag}
                            onRemoveTag={handleRemoveResourceTag}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        Select cities where you want to run your events
                        <MultiSelect
                            className="w-fit"
                            options={cityOptions}
                            maxBadgesLimit={50}
                            onChange={setSelectedCities}
                            hasSelectAll={true}
                        />
                    </div>
                </div>
            </FormProvider>
        </Modal>
    );
};

export default BatchEventsModal;
